<!--
  Mobile Filter Modal Component Template
  =====================================
  
  Advanced filtering and sorting modal template for mobile and desktop interfaces.
  This template provides a comprehensive filtering interface that supports multiple
  operation types, dynamic form field selection, and real-time filter application.
  
  Key Features:
  - Dynamic form field-based filtering with multiple operation types
  - Advanced sorting with field and direction selection
  - Form type-specific filtering with dropdown selection
  - Date and number field comparison operations
  - Real-time filter application and state management
  - Visual indicators for active filters and sort states
  
  Layout Structure:
  1. Filter Options Section - Form field selection, operation types, and value input
  2. Sort Options Section - Field and direction selection for sorting
  3. Quick Actions Section - Reset all functionality when filters/sorts are active
  
  Filter Operations:
  - Text fields: Equals, Contains
  - Date/Number fields: Equals, Contains, Greater Than, Less Than
  - Form Type: Dropdown selection with API format conversion
  
  Sort Capabilities:
  - Field-based sorting with ascending/descending options
  - Visual indicators for active sort state
  - Default sort direction management
  
  @author MacSafety Development Team
  @version 1.0
-->

<ion-card class="no-card-style">
    
    <!-- Modal Header -->
    <ion-card-header>
      <ion-card-title>Filter & Sort</ion-card-title>
    
    </ion-card-header>

    <ion-card-content class="ion-padding-horizontal filter-content">
      
      <!-- Filter Section - Column-based filtering with multiple operations -->
      <ion-item-group class="ion-margin-bottom">
        <ion-item-divider color="light">
          <ion-label>
            <h3>Filter Options</h3>
          </ion-label>
          <!-- Active Filter Indicator - Shows when filters are applied -->
          <ion-badge 
            *ngIf="hasActiveFilter" 
            color="primary" 
            slot="end">
            Active
          </ion-badge>
        </ion-item-divider>
        
        <ion-list inset lines="inset">
          <!-- Filter Column Selection - Choose which column to filter by -->
          <ion-item lines="none" 
          class="ion-full-width">
            <ion-label position="stacked">
              <strong>Filter By Column</strong>
            </ion-label>
            <ion-select
              [(ngModel)]="localFilterColumn"
              (ionChange)="onFilterColumnChange()"
              placeholder="Select column"
              interface="action-sheet"
              class="ion-full-width"
            >
              <ion-select-option *ngFor="let column of columns" [value]="column.key">
                {{ column.label }}
              </ion-select-option>
            </ion-select>
          </ion-item>

          <!-- Filter Operation Selection - Choose filter operation type -->
          <ion-item *ngIf="localFilterColumn && localFilterColumn !== 'formType'" lines="none" 
          class="ion-full-width">
            <ion-label position="stacked">
              <strong>Filter Type</strong>
            </ion-label>
            <ion-select
              [(ngModel)]="localFilterOperation"
              placeholder="Select operation"
              interface="action-sheet"
              class="ion-full-width"
            >
              <ion-select-option *ngFor="let operation of getAvailableOperations()" [value]="operation.value">
                {{ operation.label }}
              </ion-select-option>
            </ion-select>
          </ion-item>
          
          <!-- Filter Value Input - Enter the value to filter by -->
          <ion-item *ngIf="localFilterColumn" lines="none"
          class="ion-full-width">
            <ion-label position="stacked">
         <strong>Filter Value</strong>
            </ion-label>
            
            <!-- Regular Input for most columns - Text, date, number fields -->
            <ion-input
              *ngIf="localFilterColumn !== 'formType'"
              [(ngModel)]="localFilterValue"
              [placeholder]="getFilterPlaceholder(getSelectedColumnType())"
              [type]="getFilterInputType(getSelectedColumnType())"
              (ionInput)="localFilterValue = $event.detail.value || ''"
              (keydown.enter)="onFilter()"
              class="ion-full-width">
            </ion-input>

            <!-- Form Type Select - Special dropdown for form type filtering -->
            <ion-select 
              *ngIf="localFilterColumn === 'formType'"
              [(ngModel)]="localFilterValue"
              (ionChange)="onFilterApplyFormType($event, localFilterColumn)"
              placeholder="Select form type"
              interface="popover"
              class="ion-text-end">
              <ion-select-option *ngFor="let type of formTypes" [value]="type.formType">
                {{ type.title }}
              </ion-select-option>
            </ion-select>
          </ion-item>

          <!-- Filter Action Buttons - Apply and clear filter actions -->
          <ion-item *ngIf="localFilterColumn" lines="none"
          class="ion-full-width">
            <div  class="ion-display-flex ion-align-items-center">
                             <!-- Apply Filter Button - Submit the current filter -->
               <ion-button 
                 *ngIf="localFilterColumn !== 'formType'"
                 fill="solid" 
                 color="primary"
                 size="small"
                 shape="round"
                 (click)="onFilter()"
                 [disabled]="!localFilterValue || !localFilterValue.trim()"
                 title="Apply the current filter to search results"
                 >
                 <ion-icon name="search-outline" slot="start"></ion-icon>
                 Apply
               </ion-button>
              
                             <!-- Clear Filter Button - Remove active filter -->
               <ion-button 
                 *ngIf="hasActiveFilter"
                 fill="outline" 
                 color="medium"
                 size="small"
                 shape="round"
                 (click)="onClearFilter()"
                 title="Clear the current filter and show all results">
                 <ion-icon name="close-circle-outline" slot="start"></ion-icon>
                 Clear
               </ion-button>
            </div>
          </ion-item>
        </ion-list>
      </ion-item-group>

      <!-- Sort Section - Column-based sorting with direction selection -->
      <ion-item-group class="ion-margin-bottom">
        <ion-item-divider color="light">
          <ion-label>
            <strong>Sort Options</strong>
          </ion-label>
          <!-- Sort State Indicator - Shows current sort column and direction -->
          <ion-badge 
            *ngIf="localSortColumn" 
            color="secondary" 
            slot="end">
            {{ localSortDirection === 'asc' ? 'A-Z' : 'Z-A' }}
          </ion-badge>
        </ion-item-divider>
        
        <ion-list inset lines="none" class="ion-full-width">
          <!-- Sort Column Selection - Choose which column to sort by -->
          <ion-item lines="none"
          class="ion-full-width">
            <ion-label position="stacked">
              <strong>Sort By Column</strong>
            </ion-label>
            <ion-select
              [(ngModel)]="localSortColumn"
              (ionChange)="onSortColumnChange()"
              placeholder="Select column"
              interface="action-sheet"
              class="ion-full-width">
              <ion-select-option *ngFor="let column of columns" [value]="column.key">
                {{ column.label }}
              </ion-select-option>
            </ion-select>
            </ion-item>
          
          <!-- Sort Direction Selection - Choose ascending or descending order -->
          <ion-item *ngIf="localSortColumn" lines="none"
          class="ion-full-width">
            <ion-label position="stacked">
        <strong>   Sort Direction</strong>

            </ion-label>
            <ion-select
              [(ngModel)]="localSortDirection"
              (ionChange)="onSortDirectionChange()"
              interface="popover"
              placeholder="Select direction"
              class="ion-full-width">
              <ion-select-option value="asc">
                <ion-icon name="arrow-up-outline" slot="start"></ion-icon>
                Ascending (A-Z)
              </ion-select-option>
              <ion-select-option value="desc">
                <ion-icon name="arrow-down-outline" slot="start"></ion-icon>
                Descending (Z-A)
              </ion-select-option>
            </ion-select>
            </ion-item>

          <!-- Sort Action Buttons - Clear sort functionality -->
          <ion-item *ngIf="localSortColumn" lines="none"
          class="ion-full-width">
  
                         <ion-button 
               fill="outline" 
               color="medium"
               size="small"
               shape="round"
               (click)="onClearSort()"
               slot="start"
               title="Clear the current sort order">
               <ion-icon name="close-circle-outline" slot="start"></ion-icon>
               Clear Sort
             </ion-button>
          </ion-item>
        </ion-list>
      </ion-item-group>

      <!-- Quick Actions Section - Reset all functionality when filters/sorts are active -->
      <ion-item-group *ngIf="hasActiveFilter || localSortColumn">
        <ion-item-divider color="light">
          <ion-label>
            <h3>Quick Actions</h3>
          </ion-label>
        </ion-item-divider>
        
        <ion-list inset lines="none" class="ion-full-width">
          <ion-item lines="none">
           
                         <ion-button 
               fill="solid" 
               color="danger"
               size="small"
               shape="round"
               (click)="onClearAll()"
               slot="start"
               title="Clear all filters and sort settings">
               <ion-icon name="refresh-outline" slot="start"></ion-icon>
               Reset All
             </ion-button>
          </ion-item>
        </ion-list>
      </ion-item-group>

      <!-- Bottom Spacing - Ensures proper modal spacing -->
      <div class="ion-padding"></div>
      
    </ion-card-content>

  </ion-card>
